// Loads the current user's own capability-profile assignments and the profile
// docs they reference. Reads Firestore directly rather than going through a
// callable — the rules permit self-read on users/{uid} and authed-read on
// capabilityProfiles/{id}, and skipping the callable saves a cold-start on
// every /app boot for users who have profiles assigned.
//
// Called from AppView before licence issuance. The output feeds the picker
// (which profile names to show) and the choice fed into generateLicense.

import { doc, getDoc, getDocs, query, where, documentId, collection } from 'firebase/firestore'
import { ref } from 'vue'

import { db } from '../firebase'
import type { CapabilityProfile, CapabilitySet } from '../types/callables'

export interface MyProfilesResult {
  profiles:       CapabilityProfile[]
  defaultProfile: string | null
}

/**
 * Cheap best-effort validation: the profile doc's `capabilities` field passed
 * through the same shape check on write, but a hand-edited doc or a very old
 * pre-Phase-1 write could still reach here. Defaulting to an empty deny-list
 * (unrestricted) here would be wrong — the fail-closed rule lives on the
 * server, so we just render what we've got and let generateLicense refuse.
 */
function readCapabilities(raw: unknown): CapabilitySet {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>
    if ((obj.mode === 'allowList' || obj.mode === 'denyList') && Array.isArray(obj.list)) {
      return { mode: obj.mode, list: obj.list.filter((x): x is string => typeof x === 'string') as CapabilitySet['list'] }
    }
  }
  return { mode: 'denyList', list: [] }
}

export function useMyProfiles() {
  const loading = ref(false)
  const error   = ref<string | null>(null)

  async function load(uid: string): Promise<MyProfilesResult> {
    loading.value = true
    error.value = null
    try {
      const userSnap = await getDoc(doc(db, 'users', uid))
      const rawProfiles = userSnap.exists() ? userSnap.get('profiles') : undefined
      const ids: string[] = Array.isArray(rawProfiles)
        ? (rawProfiles as unknown[]).filter((x): x is string => typeof x === 'string')
        : []

      if (ids.length === 0) return { profiles: [], defaultProfile: null }

      const rawDefault = userSnap.get('defaultProfile')
      const defaultProfile = typeof rawDefault === 'string' && rawDefault ? rawDefault : null

      // Fetch profile docs in one query. Firestore's `in` operator caps at 30
      // ids per query; a single user with more assigned profiles than that is
      // absurd, but chunk defensively so a future bump doesn't silently drop
      // rows.
      const chunks: string[][] = []
      for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30))
      const results = await Promise.all(chunks.map((chunk) =>
        getDocs(query(collection(db, 'capabilityProfiles'), where(documentId(), 'in', chunk))),
      ))

      const found = new Map<string, CapabilityProfile>()
      for (const snap of results) {
        snap.forEach((s) => {
          found.set(s.id, {
            id:           s.id,
            name:         typeof s.get('name') === 'string' ? s.get('name') as string : '',
            description:  typeof s.get('description') === 'string' ? s.get('description') as string : '',
            capabilities: readCapabilities(s.get('capabilities')),
          })
        })
      }

      // Preserve the user's assignment order — that's the order the picker
      // displays profiles in, and the source of the `profiles[0]` fallback the
      // server uses when neither an explicit choice nor a default is set.
      const profiles = ids
        .map((id) => found.get(id))
        .filter((p): p is CapabilityProfile => p !== undefined)

      return { profiles, defaultProfile }
    } catch (e) {
      error.value = (e as Error).message || 'Could not load your profiles.'
      return { profiles: [], defaultProfile: null }
    } finally {
      loading.value = false
    }
  }

  return { loading, error, load }
}
